import React, { useState, useEffect, useContext } from 'react';
import { Components } from '../Lessons/styled';
import ProjectContainer from '../../components/containers/ProjectContainer';
import { calculateLessonStats, getAllLessonStats } from '../../api/lessonStats';
import { ActivityIndicatorContext } from '../../providers/ActivityIndicatorProvider/context';
import ProjectTableHeader from '../../components/ProjectTableHeader';
import ProjectTable from '../../components/ProjectTable';
import { filteringModel, tableColumns } from '../../components/ProjectTable/constants';
import useFormTextState from '../../hooks/useFormTextState';
import { ProjectTableType } from '../../components/ProjectTable/types';
import { LessonStats as LessonStatsType } from '../../api/types/lessonStats';
import styled from 'styled-components';
import AppCSS from '../../utils/styled';

const ToggleButton = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  ${AppCSS.fonts.label12Bold};
  color: ${(p) => (p.isActive ? AppCSS.colors.white : AppCSS.colors.blues.dark)};
  background-color: ${(p) => (p.isActive ? AppCSS.colors.blues.dark : AppCSS.colors.white)};
  border: 1px solid ${AppCSS.colors.blues.dark};
  ${AppCSS.radiuses.small};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(p) => (p.isActive ? AppCSS.colors.blues.dark : AppCSS.colors.blues.veryLight)};
  }
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid ${AppCSS.colors.blues.dark};
  ${AppCSS.radiuses.small};
  overflow: hidden;
`;

const LessonStats: React.FC = () => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const [stats, setStats] = useState<LessonStatsType[]>([]);
  const [showRecentStats, setShowRecentStats] = useState(false);
  const searchProps = useFormTextState();

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const statsData = await getAllLessonStats();
      console.log('Stats data:', statsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching lesson stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleCalculateStats = async () => {
    try {
      setIsLoading(true);
      await calculateLessonStats();
      await fetchStats();
    } catch (error) {
      console.error('Error calculating lesson stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStats = () => {
    setShowRecentStats(!showRecentStats);
  };

  const _filteringModel = filteringModel([
    { columnField: 'searchValue', value: searchProps.value, operatorValue: 'contains' },
  ]);
  const _sortingModel = { field: showRecentStats ? 'recentScore' : 'totalScore', sort: 'desc' };

  // Create dynamic columns based on toggle state
  const getDynamicColumns = () => {
    const baseConfig = tableColumns.lessonStats();
    const actionCounts = showRecentStats ? 'recentActionCounts' : 'totalActionCounts';

    const dynamicColumns = baseConfig.columns.map((column) => {
      if (column.field === 'totalScore') {
        return {
          ...column,
          field: showRecentStats ? 'recentScore' : 'totalScore',
          headerName: showRecentStats ? 'Recent Score' : 'Total Score',
        };
      }

      // Update action count columns to use the correct data source
      if (
        ['likes', 'superLikes', 'dislikes', 'notes', 'favorites', 'completed', 'plays', 'skips'].includes(column.field)
      ) {
        return {
          ...column,
          valueGetter: (params: any) => {
            const actionMap: { [key: string]: string } = {
              likes: 'like',
              superLikes: 'superLike',
              dislikes: 'dislike',
              notes: 'takeNote',
              favorites: 'addFavorite',
              completed: 'completed',
              plays: 'play',
              skips: 'skip',
            };
            const actionKey = actionMap[column.field];
            return params.row[actionCounts]?.[actionKey] || 0;
          },
        };
      }

      return column;
    });

    return {
      ...baseConfig,
      columns: dynamicColumns,
    };
  };

  return (
    <ProjectContainer title='Lesson Statistics'>
      <Components.Container>
        <ProjectTableHeader
          onSearchChange={searchProps.onChangeText}
          searchLabel='Search lesson stats'
          searchPlaceholder='Enter search term'>
          <Components.SearchBarChildrenWrapper>
            <Components.SearchBarSeparator />
            <ToggleWrapper>
              <ToggleButton isActive={!showRecentStats} onClick={handleToggleStats}>
                Total Stats
              </ToggleButton>
              <ToggleButton isActive={showRecentStats} onClick={handleToggleStats}>
                Recent Stats
              </ToggleButton>
            </ToggleWrapper>
            <Components.SearchBarSeparator />
            <Components.PrimaryButton onClick={handleCalculateStats}>Calculate Stats</Components.PrimaryButton>
          </Components.SearchBarChildrenWrapper>
        </ProjectTableHeader>

        <Components.TableWrapper>
          <ProjectTable
            data={stats}
            columns={getDynamicColumns()}
            filteringModel={_filteringModel}
            sortingModel={_sortingModel}
            showRowCountText='Found lesson stats'
          />
        </Components.TableWrapper>
      </Components.Container>
    </ProjectContainer>
  );
};

export default LessonStats;
